export type OrganizationRole = 'owner' | 'admin' | 'member';

export type OrgTier = 'free' | 'pro' | 'enterprise';

export type SchedulerPolicy = 'fifo' | 'priority';

export type SubscriptionStatus = 'active' | 'past_due' | 'suspended' | 'canceled';

export type BillingProvider = 'manual' | 'stripe' | '2checkout';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  domains?: OrganizationDomain[];
  avatar_url?: string;
  is_domain_verified: boolean;
  jit_enabled: boolean;
  created_at: string;
  updated_at: string;

  // Tier + subscription (read-only; mutate via billing endpoints)
  tier?: OrgTier;
  owner_user_id?: string;
  seat_count?: number;
  sso_addon_enabled?: boolean;
  billing_provider?: BillingProvider;
  billing_customer_id?: string;
  billing_subscription_id?: string;
  subscription_status?: SubscriptionStatus;
  subscription_period_end?: string | null;
  tier_changed_at?: string | null;

  // Credits
  credit_balance?: number;

  // Scheduler knobs
  min_cores_milli?: number;
  max_cores_milli?: number;
  max_queue_size?: number;
  scheduler_policy?: SchedulerPolicy;
}

export interface OrganizationDomain {
  id: string;
  organization_id: string;
  domain: string;
  is_verified: boolean;
  verification_token?: string | null;
  created_at: string;
  // Optional for UI helper
  dns_host?: string;
  dns_value?: string;
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  role: OrganizationRole;
  joined_at: string;
  // Additional fields usually included in member lists
  email?: string;
  full_name?: string;
}

export interface OrganizationCredits {
  organization_id: string;
  balance: number;
  expiry: string;
}

export interface OrganizationDetail extends Organization {
  members: OrganizationMember[];
  credits: OrganizationCredits;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: OrganizationRole;
  organization_id: string;
  created_at: string;
  expires_at: string;
}

export interface TransferOwnershipRequest {
  new_owner_id: string;
  password: string;
}

export interface CreateOrganizationRequest {
  name: string;
  domain?: string;
  jit_enabled?: boolean;
}

export interface UpdateOrganizationRequest {
  name?: string;
  domain?: string;
  avatar_url?: string;
  jit_enabled?: boolean;
  // Owner or admin: scheduler knobs. Validated server-side (min ≥ tier
  // baseline, max ≤ platform ceiling, min ≤ max, queue ≥ 1).
  scheduler_policy?: SchedulerPolicy;
  min_cores_milli?: number;
  max_cores_milli?: number;
  max_queue_size?: number;
}

/**
 * Live per-org scheduler status from asab-agents /scheduler/status endpoint.
 * Populated by the Rust stateless scheduler (valkey mode) or tier defaults.
 */
export interface SchedulerStatus {
  org_id: string;
  active: number;
  current_slots: number;
  current_cores_milli: number;
  min_cores_milli: number;
  max_cores_milli: number;
  queued: number;
  max_queue_size: number;
  policy: SchedulerPolicy;
  saturation_pct: number;
  queue_pct: number;
  core_overage_microcents_per_hr: number;
  admitted_total: number;
  rejected_queue_full: number;
  tier: OrgTier;
  scheduler_mode: 'live' | 'tier_default';
}

/**
 * User profile badges — signup grant + payment method on file.
 * Served by asab-identity GET /v1/users/me/profile.
 */
export interface UserProfile {
  user_id: string;
  signup_credits_granted: boolean;
  payment_method_on_file: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface InviteMemberRequest {
  email: string;
  role: OrganizationRole;
}

export interface UpdateMemberRoleRequest {
  role: Exclude<OrganizationRole, 'owner'>;
}

// ── Org Settings (model roles) ────────────────────────────────────────────

/** Wire key union for all system model roles. */
export type ModelRoleKey = 'guardrail_judge' | 'search_answer' | 'search_summary' | 'moderation';

/** Human-readable labels for each role, shown in the System Models UI. */
export const MODEL_ROLE_LABELS: Record<ModelRoleKey, string> = {
  guardrail_judge: 'Guardrail Judge',
  search_answer: 'Search Answer',
  search_summary: 'Search Summary',
  moderation: 'Moderation',
};

/** All role keys in display order. */
export const ALL_MODEL_ROLE_KEYS: ModelRoleKey[] = [
  'guardrail_judge',
  'search_answer',
  'search_summary',
  'moderation',
];

/** Per-org settings document. model_roles keys are ModelRoleKey; empty string = platform default. */
export interface OrgSettings {
  model_roles: Partial<Record<ModelRoleKey, string>>;
}

/** PUT body: PATCH-merge semantics (key="" removes override, absent key unchanged). */
export type UpdateOrgSettingsRequest = OrgSettings;
